/*
 * Wire shapes of the social-export API, mirroring valoi_engine's `app/datatypes.py`
 * ("Social exports"). Kept in the engine's snake_case: this slice is a thin view over
 * them and a second, camelCase copy would only be one more place to drift.
 *
 * Export status is deliberately its own union and NOT the shared ProcessingStatus: an
 * export is not a generation job and must never be mistaken for one.
 */

export type ExportStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'expired'
  | 'deleted';

export type ExportPreset = {
  key: string;
  version: number;
  platform: string;
  content_type: string;
  label: string;
  profile: string;
  canvas_width: number;
  canvas_height: number;
  summary: string;
  min_duration_seconds?: number | null;
  max_duration_seconds?: number | null;
  max_file_bytes?: number | null;
  /** Short, user-facing limits. Documentation research is not part of the API. */
  limitations: string[];
  available: boolean;
  unavailable_code?: string | null;
  unavailable_reason?: string | null;
  warnings: string[];
};

export type ExportPresetList = {
  presets: ExportPreset[];
  retention_days: number;
  layout_note: string;
  video_runtime_seconds?: number | null;
};

export type ExportOutput = {
  mode: string;
  width?: number | null;
  height?: number | null;
  fps?: number | null;
  duration_seconds?: number | null;
  size_bytes?: number | null;
};

export type ExportRecord = {
  id: string;
  video_id: string;
  preset: string;
  preset_version: number;
  profile: string;
  status: ExportStatus;
  created_at: string;
  updated_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  expires_at?: string | null;
  output?: ExportOutput | null;
  error?: { code: string; message: string } | null;
  reused: boolean;
};

export type ExportList = { exports: ExportRecord[] };

export type ExportDownload = { url: string; filename: string; expires_at?: string | null };

export const ACTIVE_EXPORT_STATUSES: readonly ExportStatus[] = ['queued', 'processing'];

export function isActiveExport(e: { status: string } | null | undefined): boolean {
  return Boolean(e && (e.status === 'queued' || e.status === 'processing'));
}

export function isTerminalExport(e: { status: string }): boolean {
  return !isActiveExport(e);
}
